// Locale display formatters for dates, extracted from the copies the table
// column modules, drawers and profile pages each used to define. Every body is
// the verbatim original, so swapping a call site for one of these changes
// nothing on screen.
//
// FOUR exports, not one: the pairs below differ in real output and must not be
// folded together. `fmtDate` vs `fmtDatePadded` disagree on single-digit days
// ("Nov 5" vs "Nov 05") AND on what an unparseable value renders as (the input
// echoed back vs a dash).
//
// These read `i18n.language` at call time, so a language switch re-renders into
// the new locale. `fmtRelative` takes `t` as a parameter instead of importing
// it — the repo's rule for helpers, and what lets a test pass a spy.

import i18n from '@/i18n/config';

const MIN_MS = 60 * 1000;
const HOUR_MS = 60 * MIN_MS;
const DAY_MS = 24 * HOUR_MS;

/** "29 Jul 2026, 08:40" in the active locale. Dash when unset, the input back
 *  when unparseable. */
export const fmtDateTime = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

/** "Nov 5, 2025" in the active locale — an unpadded day. Dash when unset, the
 *  input back when unparseable. */
export const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(i18n.language, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
};

/** "Nov 05, 2025" in the active locale — a PADDED day, and a dash rather than
 *  the raw input when unparseable. Both differences are why this is its own
 *  export and not a flag on fmtDate. */
export const fmtDatePadded = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(i18n.language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
};

/** "40m ago" / "6h ago" / "Yesterday" / "3 days ago" / "2 weeks ago". */
export const fmtRelative = (
  iso: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'always' });
  if (diff < HOUR_MS) return rtf.format(-Math.max(1, Math.round(diff / MIN_MS)), 'minute');
  if (diff < DAY_MS) return rtf.format(-Math.round(diff / HOUR_MS), 'hour');
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday - DAY_MS && d.getTime() < startOfToday)
    return t('forms_last_yesterday');
  const days = Math.round(diff / DAY_MS);
  if (days < 7) return rtf.format(-days, 'day');
  if (days < 30) return rtf.format(-Math.round(days / 7), 'week');
  if (days < 365) return rtf.format(-Math.round(days / 30), 'month');
  return rtf.format(-Math.round(days / 365), 'year');
};
